import classNames from "classnames";
import Downshift, { ControllerStateAndHelpers, DownshiftProps, DownshiftState } from "downshift";
import { observer } from "mobx-react";
import React from "react";
import { L10n } from "../../../i18n";
import { AgentButton } from "../../inputs/buttons/agentButton/agentButton";
import { TooltipComponent } from "../../tooltipComponent";
import inputStyles from "../agentInput.css";
import styles from "./autoComplete.css";

export type AutoCompleteItem = object | string | number;

interface InputProps {
  wrapperClassName?: string;
  toggleButtonProps?: any;
  inputProps: React.HTMLProps<HTMLInputElement>;
  isOpen?: boolean;
  overrideInputArrowIsOpen?: boolean;
  onClearSelected?(): void;
  error?: string;
  hasError?: boolean;
}

const AutoCompleteInput: React.SFC<InputProps> = observer(
  ({
    wrapperClassName,
    toggleButtonProps,
    isOpen,
    overrideInputArrowIsOpen,
    onClearSelected,
    error,
    hasError,
    inputProps
  }) => {
    const errorClassNames = error || hasError ? inputStyles["has-error"] : undefined;

    return (
      <div {...toggleButtonProps} className={classNames(wrapperClassName, errorClassNames)}>
        <input {...inputProps} />
        {error && (
          <TooltipComponent
            positionPreset={"top right"}
            text={error}
            maxWidth={"700%"}
            width={"600%"}
            style={"error"}
          >
            <i className={["icon-exclamation-mark", inputStyles["error-feedback"]].join(" ")} />
          </TooltipComponent>
        )}
        {inputProps.readOnly && onClearSelected && (
          <div
            className={styles["icon-close-cross-container"]}
            onClick={(e) => {
              onClearSelected();
              e.stopPropagation();
            }}
          >
            <i className={classNames("icon-close-cross", styles["icon-close-cross"])} />
          </div>
        )}
        {inputProps.readOnly && (
          <div
            className={classNames(styles["icon-arrow"], {
              [styles["is-open"]]:
                overrideInputArrowIsOpen !== undefined ? overrideInputArrowIsOpen : isOpen
            })}
          >
            <i className={"icon-arrow"} />
          </div>
        )}
      </div>
    );
  }
);

export type AutoCompleteProps<T> = {
  GridItem?: React.ComponentType<{ item: T } & Pick<DownshiftState<T>, "inputValue">>;
  styleType?: "signup";
  /** Callback invoked on option selection */
  onChange?: (value: T, stateAndHelpers: ControllerStateAndHelpers<T>) => void;
  /** Callback invoked on query input change */
  onInputValueChange?: (value: string, stateAndHelpers: ControllerStateAndHelpers<T>) => void;
  itemToString?: (item: T | null) => string;
  itemsProvider: (helpers: ControllerStateAndHelpers<T>) => T[];
  renderQueryInput?: (helpers: ControllerStateAndHelpers<T>) => JSX.Element;
  renderMenu?: (helpers: ControllerStateAndHelpers<T>) => JSX.Element;
  renderItem?: (item: T, index: number, helpers: ControllerStateAndHelpers<T>) => JSX.Element;
  onInputBlur?: React.HTMLProps<HTMLInputElement>["onBlur"];
  onInputClick?: React.HTMLProps<HTMLInputElement>["onClick"];
  onBlur?: () => void;
  enableReset?: boolean;
  error?: string;
  /** Explicitly style dropdown for error, if possible provide `error` message instead */
  hasError?: boolean;
  placeholder?: string;
  className?: string;
  menuClassName?: string;
  emptyPlaceholder?: string;
  inputContainerClassName?: string;
  itemClassName?: string;
  readOnly?: boolean;
  autoFocus?: boolean;
  overrideInputArrowIsOpen?: boolean;
  onClearSelected?: () => void;
  onOutsideClick?: (ev?: any) => void;

  // also expose selected Downshift props
} & Pick<
  DownshiftProps<T>,
  | "isOpen"
  | "stateReducer"
  | "onStateChange"
  | "inputValue"
  | "initialInputValue"
  | "initialIsOpen"
  | "initialHighlightedIndex"
  | "defaultIsOpen"
>;

export const AutoComplete = observer(function AutoCompleteComponent<T extends AutoCompleteItem>(
  props: React.PropsWithChildren<AutoCompleteProps<T>>
) {
  const itemToString = props.itemToString || ((item: T | null): string => (item || "").toString());

  const handleChange = (item: T | null, helpers: ControllerStateAndHelpers<T>): void => {
    if (item === null) {
      return;
    }
    const { onChange } = props;
    if (onChange) {
      onChange(item, helpers);
    }
  };

  const onInputValueChange = (value: string, stateAndHelpers: ControllerStateAndHelpers<T>) => {
    const { onInputValueChange: onInputChange } = props;
    if (onInputChange) {
      onInputChange(value, stateAndHelpers);
    }
  };

  /**
   * Render query input element.
   */
  const renderQueryInput =
    props.renderQueryInput ||
    ((helpers: ControllerStateAndHelpers<T>) => {
      const { getInputProps, isOpen, getToggleButtonProps } = helpers;
      const {
        placeholder,
        readOnly,
        autoFocus,
        styleType,
        onInputBlur,
        onInputClick,
        overrideInputArrowIsOpen,
        error,
        onClearSelected,
        hasError,
        inputContainerClassName
      } = props;
      const inputProps = getInputProps({
        placeholder,
        readOnly,
        autoFocus,
        onBlur: onInputBlur,
        onClick: onInputClick
      });
      const styleClassName = styleType === "signup" ? inputStyles.signup : undefined;
      const toggleButtonProps = getToggleButtonProps();
      return (
        <AutoCompleteInput
          toggleButtonProps={toggleButtonProps}
          inputProps={inputProps}
          isOpen={isOpen}
          wrapperClassName={classNames(
            inputStyles["input-wrap"],
            styleClassName,
            inputContainerClassName
          )}
          error={error}
          hasError={hasError}
          overrideInputArrowIsOpen={overrideInputArrowIsOpen}
          onClearSelected={onClearSelected}
        />
      );
    });

  /**
   * Render dropdown menu element.
   */
  const renderMenu =
    props.renderMenu ||
    ((helpers: ControllerStateAndHelpers<T>) => {
      const { getMenuProps } = helpers;
      const items = props.itemsProvider(helpers);
      const menuProps = getMenuProps({
        className: classNames(styles["menu-container"], props.menuClassName, {
          [styles.empty]: !items.length
        })
      });

      return (
        <div {...menuProps}>
          {props.children && <div className={styles["children-container"]}>{props.children}</div>}
          {items.length ? (
            <ul>{items.map((item, index) => renderItem(item, index, helpers))}</ul>
          ) : (
            <div className={styles["no-results"]}>
              <i className={"icon-exclamation-mark"} />
              <p>{props.emptyPlaceholder}</p>
            </div>
          )}
          {renderReset(helpers)}
        </div>
      );
    });

  /**
   * Render dropdown menu item.
   */
  const renderItem =
    props.renderItem ||
    ((item: T, index: number, state: ControllerStateAndHelpers<T>) => {
      const { getItemProps, highlightedIndex, selectedItem } = state;
      const { GridItem } = props;
      const isHighlighted = highlightedIndex === index;
      const isSelected = selectedItem === item;
      const itemProps = getItemProps({ key: index, index, item });

      return (
        <li {...itemProps} className={classNames(styles["default-li"])}>
          <div
            className={classNames(styles["default-grid"], props.itemClassName, {
              [styles.highlighted]: isHighlighted,
              [styles.selected]: isSelected
            })}
          >
            {!!GridItem ? (
              <GridItem item={item} inputValue={state.inputValue} />
            ) : (
              itemToString(item)
            )}
          </div>
        </li>
      );
    });

  /**
   * Render reset UI at the end of the menu.
   */
  const renderReset = (helpers: ControllerStateAndHelpers<T>) => {
    const { clearSelection } = helpers;
    const itemsCount = props.itemsProvider(helpers).length;
    return (
      props.enableReset && (
        <div className={styles.clear}>
          <AgentButton
            tabIndex={-1}
            buttonStyle={itemsCount ? "transparent primary" : "transparent error"}
            fullWidth
            small
            onClick={(ev) => clearSelection()}
          >
            <L10n id={"profile.edit.clearFilter"} />
          </AgentButton>
        </div>
      )
    );
  };

  const renderDownshift = (helpers: ControllerStateAndHelpers<T>) => {
    const { isOpen } = helpers;
    const { className, styleType, onBlur } = props;
    const styleClassName = styleType === "signup" ? styles.signup : undefined;

    return (
      <div className={classNames(styles.container, styleClassName, className)} onBlur={onBlur}>
        {renderQueryInput(helpers)}
        {isOpen && renderMenu(helpers)}
      </div>
    );
  };

  const render = () => {
    const {
      isOpen,
      stateReducer,
      inputValue,
      initialIsOpen,
      initialHighlightedIndex,
      onStateChange,
      defaultIsOpen,
      onOutsideClick
    } = props;

    return (
      <Downshift<T>
        initialHighlightedIndex={initialHighlightedIndex}
        onChange={handleChange}
        onInputValueChange={onInputValueChange}
        itemToString={itemToString}
        inputValue={inputValue}
        isOpen={isOpen}
        stateReducer={stateReducer}
        initialIsOpen={initialIsOpen}
        onStateChange={onStateChange}
        defaultIsOpen={defaultIsOpen}
        onOuterClick={onOutsideClick}
      >
        {(helpers) => renderDownshift(helpers)}
      </Downshift>
    );
  };

  return render();
});
